// Plan finder: turns a short questionnaire into a plan recommendation.
//
// Every number here is read from the public offer (marketing-plan-matrix.ts,
// currency.ts). Nothing is invented: limits come from PLAN_MATRIX rows, prices
// from PLAN_MONTHLY_EUR / PLAN_SETUP_EUR, top-ups from CREDIT_PACKS. The only
// modelling assumption is the credits-per-article estimate, which mirrors the
// "1 article ≈ 3 crédits" hint in the matrix for standard quality and the
// per-model credit figures used on /fonctionnalites for the higher tiers.

import {
  CREDIT_PACKS,
  PLAN_MATRIX,
  type CellValue,
  type CreditPackEntry,
  type PlanSlug,
} from "@/lib/marketing-plan-matrix";
import { annualMonthlyEquivalentCents, planMonthlyCents, planSetupCents } from "@/lib/currency";

export type QualityTier = "standard" | "advanced" | "premium";

export type NeedKey =
  | "hourly"
  | "realtime"
  | "qa"
  | "api_write"
  | "webhooks"
  | "white_label"
  | "ads"
  | "programmatic"
  | "priority_support";

export interface PlanFinderInput {
  /** Articles published per month, all sites and languages combined. */
  articlesPerMonth: number;
  sites: number;
  languages: number;
  seats: number;
  quality: QualityTier;
  needs: NeedKey[];
  annual: boolean;
}

/** Credits per published article, by quality tier (estimate, see header). */
// Per the billed catalogue (ai_models): only the rewrite call is charged per
// article, plus the image model when one is generated. Standard = Haiku 3 with
// a stock image; advanced = Sonnet 8 + Flux Schnell 2; premium = Opus 18 +
// Flux Ultra 8.
export const CREDITS_PER_ARTICLE: Record<QualityTier, number> = {
  standard: 3,
  advanced: 10,
  premium: 26,
};

/** Lowest plan that ships the model family a quality tier needs. */
const QUALITY_MIN_PLAN: Record<QualityTier, PlanSlug> = {
  standard: "starter",
  advanced: "growth",
  premium: "network",
};

/** Matrix row consulted for each optional need. */
const NEED_ROW: Record<NeedKey, string> = {
  hourly: "publish_hourly",
  realtime: "publish_realtime",
  qa: "editorial_qa",
  api_write: "api_write",
  webhooks: "api_webhooks",
  white_label: "wl_branding",
  ads: "ads",
  programmatic: "programmatic",
  priority_support: "support_priority",
};

const PAID_PLANS: PlanSlug[] = ["starter", "growth", "network", "agency"];

/**
 * Hard monthly article cap per plan, enforced by the platform
 * (package_features.max_articles_per_month, checked on insert) and shown
 * on /tarifs as the published cap. It is not the
 * same as the credit allowance: packs add credits, they do not lift this cap.
 */
export const ARTICLE_CAP: Record<PlanSlug, number> = {
  starter: 130,
  growth: 500,
  network: 1800,
  agency: 5000,
  enterprise: Number.POSITIVE_INFINITY,
};

const ROWS = new Map<string, Record<PlanSlug, CellValue>>();
for (const cat of PLAN_MATRIX) for (const row of cat.rows) ROWS.set(row.id, row.cells);

function cell(rowId: string, slug: PlanSlug): CellValue {
  const cells = ROWS.get(rowId);
  if (!cells) throw new Error(`plan-finder: unknown matrix row ${rowId}`);
  return cells[slug];
}

/** Numeric limit for a plan; Infinity for "unlimited". */
export function planLimit(rowId: string, slug: PlanSlug): number {
  const c = cell(rowId, slug);
  if (c.kind === "number") return c.value;
  if (c.kind === "unlimited") return Number.POSITIVE_INFINITY;
  return 0;
}

export function planHas(rowId: string, slug: PlanSlug): boolean {
  const c = cell(rowId, slug);
  if (c.kind === "boolean") return c.value;
  if (c.kind === "text") return true;
  return c.kind === "unlimited" || c.kind === "custom";
}

export type ConstraintKey = "articles" | "sites" | "languages" | "seats" | "quality" | NeedKey;

/** Which constraints a plan fails, ignoring credits. */
export function planViolations(slug: PlanSlug, input: PlanFinderInput): ConstraintKey[] {
  const out: ConstraintKey[] = [];
  if (input.articlesPerMonth > ARTICLE_CAP[slug]) out.push("articles");
  if (input.sites > planLimit("sites", slug)) out.push("sites");
  if (input.languages > planLimit("languages_count", slug)) out.push("languages");
  if (input.seats > planLimit("team_seats", slug)) out.push("seats");
  if (PAID_PLANS.indexOf(QUALITY_MIN_PLAN[input.quality]) > PAID_PLANS.indexOf(slug)) out.push("quality");
  for (const need of input.needs) if (!planHas(NEED_ROW[need], slug)) out.push(need);
  return out;
}

export interface PackSuggestion {
  pack: CreditPackEntry;
  count: number;
}

/** Cheapest combination of packs (up to 3 of each) covering `credits`. */
export function cheapestPacks(credits: number): { packs: PackSuggestion[]; cents: number; credits: number } | null {
  if (credits <= 0) return { packs: [], cents: 0, credits: 0 };
  let best: { packs: PackSuggestion[]; cents: number; credits: number } | null = null;
  const n = CREDIT_PACKS.length;
  const counts = new Array<number>(n).fill(0);
  const walk = (i: number) => {
    if (i === n) {
      let c = 0, cents = 0;
      const packs: PackSuggestion[] = [];
      counts.forEach((k, j) => {
        if (k > 0) {
          c += CREDIT_PACKS[j].credits * k;
          cents += CREDIT_PACKS[j].priceCents * k;
          packs.push({ pack: CREDIT_PACKS[j], count: k });
        }
      });
      if (c >= credits && (!best || cents < best.cents)) best = { packs, cents, credits: c };
      return;
    }
    for (let k = 0; k <= 3; k++) { counts[i] = k; walk(i + 1); }
    counts[i] = 0;
  };
  walk(0);
  return best;
}

export interface PlanOption {
  slug: PlanSlug;
  includedCredits: number;
  neededCredits: number;
  shortfall: number;
  packs: PackSuggestion[];
  packCents: number;
  /** Plan price for the chosen billing period, per month. */
  planCents: number;
  setupCents: number;
  totalMonthlyCents: number;
  /** Articles per month the included credits cover at the chosen quality. */
  includedArticles: number;
}

export interface PlanFinderResult {
  kind: "plan" | "enterprise";
  recommended: PlanOption | null;
  /** Next plan up, if one exists, for comparison. */
  alternative: PlanOption | null;
  neededCredits: number;
  /** Why smaller plans were ruled out, keyed by plan. */
  ruledOut: Partial<Record<PlanSlug, ConstraintKey[]>>;
  /** True when the recommendation is Enterprise because of hard limits. */
  enterpriseReasons: ConstraintKey[] | "volume" | null;
}

/** Above this many credits per month, even Agency plus packs is no longer the right shape. */
const AGENCY_PACK_CEILING = 12000 + 3 * 15000;

function buildOption(slug: PlanSlug, input: PlanFinderInput, neededCredits: number): PlanOption | null {
  const monthly = planMonthlyCents(slug);
  if (monthly == null) return null;
  const includedCredits = planLimit("monthly_credits", slug);
  const shortfall = Math.max(0, neededCredits - includedCredits);
  const top = cheapestPacks(shortfall);
  if (!top) return null;
  const planCents = input.annual ? annualMonthlyEquivalentCents(monthly) : monthly;
  const setupCents = input.annual ? 0 : (planSetupCents(slug) ?? 0);
  return {
    slug,
    includedCredits,
    neededCredits,
    shortfall,
    packs: top.packs,
    packCents: top.cents,
    planCents,
    setupCents,
    totalMonthlyCents: planCents + top.cents,
    includedArticles: Math.min(ARTICLE_CAP[slug], Math.floor(includedCredits / CREDITS_PER_ARTICLE[input.quality])),
  };
}

export function recommendPlan(input: PlanFinderInput): PlanFinderResult {
  const neededCredits = Math.max(0, Math.round(input.articlesPerMonth)) * CREDITS_PER_ARTICLE[input.quality];
  const ruledOut: Partial<Record<PlanSlug, ConstraintKey[]>> = {};
  const candidates: PlanOption[] = [];
  for (const slug of PAID_PLANS) {
    const v = planViolations(slug, input);
    if (v.length) { ruledOut[slug] = v; continue; }
    const opt = buildOption(slug, input, neededCredits);
    if (opt) candidates.push(opt);
  }

  const agencyViolations = planViolations("agency", input);
  if (agencyViolations.length) {
    return { kind: "enterprise", recommended: null, alternative: null, neededCredits, ruledOut, enterpriseReasons: agencyViolations };
  }
  if (candidates.length === 0 || neededCredits > AGENCY_PACK_CEILING) {
    return { kind: "enterprise", recommended: null, alternative: null, neededCredits, ruledOut, enterpriseReasons: "volume" };
  }

  // Cheapest total wins; on a tie prefer the plan with more headroom (fewer packs).
  candidates.sort((a, b) => a.totalMonthlyCents - b.totalMonthlyCents || a.packs.length - b.packs.length);
  const recommended = candidates[0];
  const idx = PAID_PLANS.indexOf(recommended.slug);
  const next = PAID_PLANS[idx + 1];
  const alternative = next ? candidates.find((c) => c.slug === next) ?? buildOption(next, input, neededCredits) : null;
  return { kind: "plan", recommended, alternative, neededCredits, ruledOut, enterpriseReasons: null };
}
